import type React from 'react'
import { useEffect, useState } from 'react'
import { type CookiePreferences, useCookieConsent } from '@/landing/contexts/CookieConsentContext'
import { useLanguage } from '@/landing/contexts/LanguageContext'

export const CookieBanner: React.FC = () => {
  const { t } = useLanguage()
  const {
    hasConsent,
    isLoading,
    preferences: savedPreferences,
    updatePreferences,
    showBanner,
  } = useCookieConsent()

  const [showPreferences, setShowPreferences] = useState(false)
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(savedPreferences)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!isLoading && (showBanner || !hasConsent)) {
      const timer = setTimeout(() => setIsVisible(true), 100)
      return () => clearTimeout(timer)
    }
    setIsVisible(false)
    setShowPreferences(false)
  }, [isLoading, hasConsent, showBanner])

  useEffect(() => {
    setLocalPreferences(savedPreferences)
  }, [savedPreferences])

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    }
    updatePreferences(allAccepted)
  }

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    }
    updatePreferences(onlyNecessary)
    setShowPreferences(false)
  }

  const handleSavePreferences = () => {
    updatePreferences(localPreferences)
    setShowPreferences(false)
  }

  const handlePreferenceChange = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return

    setLocalPreferences((prev) => ({
      ...prev,
      [type]: value,
    }))
  }

  if (isLoading || (hasConsent && !showBanner)) return null

  return (
    <>
      {/* Blocking overlay */}
      <div
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-700 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />

      {/* Cookie banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 shadow-2xl z-50 p-4 md:p-6 transition-all duration-700 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          {!showPreferences ? (
            <div className="flex flex-col gap-4">
              <div className="flex-1">
                <h3
                  className="text-lg font-bold mb-2 text-gray-900"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {t('cookie_banner.title')}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  {t('cookie_banner.description')}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-start mb-4">
                  <button
                    type="button"
                    onClick={() => setShowPreferences(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                  >
                    {t('cookie_banner.customize')}
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                  >
                    {t('cookie_banner.reject_all')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    {t('cookie_banner.accept_all')}
                  </button>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <a
                      href="/privacy"
                      className="text-primary hover:text-primary/80 underline transition-colors"
                    >
                      {t('footer.privacy_policy')}
                    </a>
                    <span className="text-gray-400">•</span>
                    <a
                      href="/terms"
                      className="text-primary hover:text-primary/80 underline transition-colors"
                    >
                      {t('footer.terms_of_use')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h3
                  className="text-xl font-bold text-gray-900"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {t('cookie_banner.preferences_title')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPreferences(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                  aria-label="Close preferences"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {/* Necessary Cookies */}
                <div className="bg-gray-50 rounded-md p-3 cursor-not-allowed">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4
                        className="font-semibold text-base text-gray-900"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {t('cookie_banner.necessary.title')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookie_banner.necessary.description')}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary rounded cursor-not-allowed"
                      checked={localPreferences.necessary}
                      disabled
                    />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div
                  role="button"
                  tabIndex={0}
                  className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handlePreferenceChange('analytics', !localPreferences.analytics)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePreferenceChange('analytics', !localPreferences.analytics)
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4
                        className="font-semibold text-base text-gray-900"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {t('cookie_banner.analytics.title')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookie_banner.analytics.description')}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary rounded cursor-pointer"
                      checked={localPreferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                    />
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div
                  role="button"
                  tabIndex={0}
                  className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handlePreferenceChange('marketing', !localPreferences.marketing)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePreferenceChange('marketing', !localPreferences.marketing)
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4
                        className="font-semibold text-base text-gray-900"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {t('cookie_banner.marketing.title')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookie_banner.marketing.description')}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary rounded cursor-pointer"
                      checked={localPreferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                    />
                  </div>
                </div>

                {/* Functional Cookies */}
                <div
                  role="button"
                  tabIndex={0}
                  className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handlePreferenceChange('functional', !localPreferences.functional)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePreferenceChange('functional', !localPreferences.functional)
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4
                        className="font-semibold text-base text-gray-900"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {t('cookie_banner.functional.title')}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('cookie_banner.functional.description')}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary rounded cursor-pointer"
                      checked={localPreferences.functional}
                      onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-end">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
                >
                  {t('cookie_banner.reject_all')}
                </button>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  {t('cookie_banner.save_preferences')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
